<template>
    <div>
        <v-server-table :name="tableName" :ref="'empTrainingCourseTable'" :url="dataUrl" :columns="columns" :options="options">

            <div slot="afterFilterWrapper" class="ml-md-2 ml-0 mt-md-0 mt-2 float-md-left">
                <div class="clearfix d-md-none d-block"></div>

                <div v-if="allowFilterSaving" class="float-left mr-2">
                    <user-filters ref="empCourseListSavedFilters" assetType="EmployeeTrainingCourse" :filterKey="filterKey" @saving="setUserFilterData" @show="setUserFilterData" />
                </div>

                <div class="float-left mr-2">
                    <table-filters-2 ref="empCourseListTableFilters"
                        :filterUrl="filterUrl"
                        v-bind:filters="filters"
                        :emptyFilters="filterDefs"
                        :filterKey="filterKey"
                        @filtersChanged="refreshTableData"
                        @filtersCleared="filtersCleared"
                        v-show="!dashboardFilters">
                        <template slot-scope="slotProps">
                            <div class="d-inline-block">
                                <multi-select v-model="slotProps.filters['employee.division_id']" :btnLabel="(values) => ('Divisions ('+(values ? values.length : 0)+')')" :selectOptions="slotProps.filterData.divisions" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="slotProps.filters['employee_training_courses.training_course_id']" :btnLabel="(values) => ('Courses ('+(values ? values.length : 0)+')')" :selectOptions="slotProps.filterData.courses" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="slotProps.filters['training_course_catalogs.training_catalog_id']" :btnLabel="(values) => ('Catalogs ('+values.length+')')" :selectOptions="slotProps.filterData.catalogs" :options="{multi: true}"></multi-select>
                            </div>

                            <div class="d-inline-block">
                                <date-range-selector buttonTitle="Date Completed" v-model="slotProps.filters['employee_training_courses.completed_at']" />
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector buttonTitle="Date Expires" v-model="slotProps.filters['employee_training_courses.expires_at']" />
                            </div>
                            <div class="mt-2">
                                <div class="d-inline-block" v-if="courseType !== 'Pending'">
                                    <b-form-select v-model="slotProps.filters['employee_training_courses.completed_status']"
                                        size="sm"
                                        :options="[{value: null, text: '-- Show All Completed/Pending --'}, {value: 'completed', text: 'Completed Only'}, {value: 'pending', text: 'Pending Only'}]" />
                                </div>
                                <div class="d-inline-block">
                                    <b-form-select v-model="slotProps.filters['training_course_employee.IsActive']"
                                        size="sm"
                                        :options="[{value: null, text: '-- Show Active/Inactive Employees --'}, {value: 0, text: 'Inactive Employees'}, {value: 1, text: 'Active Employees'}]" />
                                </div>
                                <div class="d-inline-block" style="width: 250px;" v-if="!this.employeeId">
                                    <employee-select :forFilters="true" :selectedValues="slotProps.filters['employee_training_courses.employee_id']"></employee-select>
                                </div>
                            </div>
                        </template>
                    </table-filters-2>
                </div>

                <div class="float-md-left" v-if="courseType !== 'Pending' && !this.dashboardFilters">
                    <div class="form-group">
                        <input type="number" min="1" name="upcoming_expiration_in_days" v-model="upcoming_expiration_in_days" placeholder="Enter Days Until Expiration" class="form-control d-inline-block" style="max-width: 75px;" />
                        <label class="badge badge-warning ml-2">Days Until Expires</label>
                    </div>
                </div>

                <div class="float-md-left ml-md-2 ml-0 mb-md-0 mb-3">
                    <button type="button" class="btn btn-light" @click="exportTable()" :disabled="exportingTable">
                        <template v-if="!exportingTable"><i class="fa fa-file-excel-o"></i> Export</template>
                        <template v-else><i class="fa fa-circle-o-notch fa-spin"></i> Exporting</template>
                    </button>


                </div>

                <div class="float-md-left ml-md-2 ml-0 mb-md-0 mb-3" v-if="showAddButton">
                    <router-link :to="`/training-courses/employee/${employeeId}/add-courses`" class="btn btn-light" title="Add a Training Course to this Employee">
                        <i class="fa fa-plus"></i> Add
                    </router-link>
                </div>

                <div class="float-md-left ml-md-2 ml-0 mb-md-0 mb-3" v-if="showDistributeButton">
                    <b-dropdown right text="Distribute" variant="light">
                        <b-dropdown-item :to="urlDistributeEmail"><i class="fa fa-envelope"></i> Email</b-dropdown-item>
                        <b-dropdown-item :to="urlDistributeText"><i class="fa fa-comment"></i> Text</b-dropdown-item>
                    </b-dropdown>
                </div>

                <div class="clearfix d-md-none d-block"></div>
            </div>

            <template slot="id" slot-scope="props">
                <router-link :to="`/training-courses/employee/${props.row.training_course_id}/${props.row.id}`" title="Edit Employee Course" class="btn btn-xs btn-secondary">
                    <i class="fa fa-edit"></i> Edit
                </router-link>

                <router-link :to="`/training-courses/employee/${props.row.id}/view`" title="View Employee Course" class="btn btn-xs btn-default">
                    <i class="fa fa-check"></i> View
                </router-link>
            </template>

            <template slot="employee_person_name" slot-scope="props">
                <router-link  v-if="props.row.employee_id" :to="`/employees/view/${props.row.employee_id}`" title="View Employee">
                    {{ props.row.employee_person_name }}
                </router-link>
                <span v-else>
                    {{ props.row.employee_person_name }}
                </span>
            </template>

            <template slot="JobsiteName" slot-scope="props">
                <router-link  v-if="props.row.jobsite_id" :to="`/jobsites/view/${props.row.jobsite_id}`" title="View Jobsite">
                    {{ props.row.JobsiteNumber }} - {{ props.row.JobsiteName }}
                </router-link>
            </template>

            <template slot="DivisionName" slot-scope="props">
                <span  v-if="props.row.DivisionName">
                    <span v-if="props.row.division_code">{{ props.row.division_code }} -</span> {{ props.row.DivisionName }}
                </span>
            </template>

            <template slot="IsActive" slot-scope="props">
                <label class="badge badge-success" v-if="props.row.IsActive === 1">Active</label>
                <label class="badge badge-warning" v-else-if="props.row.IsActive === 0">Inactive</label>
            </template>

            <router-link slot="course_name" slot-scope="props" :to="`/training-courses/view/${props.row.training_course_id}`" title="View Training Course">
                {{ props.row.course_name }}
            </router-link>

            <template slot="completed_at" slot-scope="props">
                <template v-if="props.row.completed_at">
                    {{ props.row.completed_at | moment("MM/DD/YYYY") }}
                </template>
                <template v-else>
                    <label class="badge badge-warning">Pending</label>
                </template>
            </template>

            <template slot="expires_at" slot-scope="props">
                <template v-if="props.row.expires_at">
                    <label class="badge badge-danger" v-if="isExpired(props.row.expires_at)">{{ props.row.expires_at | moment("MM/DD/YYYY") }}</label>
                    <label class="badge badge-warning" v-else-if="expiresThisMonth(props.row.expires_at)">{{ props.row.expires_at | moment("MM/DD/YYYY") }}</label>
                    <span v-else>{{ props.row.expires_at | moment("MM/DD/YYYY") }}</span>
                </template>
            </template>

            <template slot="last_texted_at" slot-scope="props">
                <span v-if="props.row.last_texted_at">{{ props.row.last_texted_at | moment('timezone', userTimezone, "MM/DD/YYYY") }}</span>
            </template>

            <template slot="last_emailed_at" slot-scope="props">
                <span v-if="props.row.last_texted_at">{{ props.row.last_emailed_at | moment('timezone', userTimezone, "MM/DD/YYYY") }}</span>
            </template>

            <template slot="has_files" slot-scope="props">
                <i class="fa fa-files-o" v-if="props.row.has_files == 1" title="Has Files Attached"></i>
            </template>

        </v-server-table>
    </div>
</template>

<script>
    import moment from 'moment-timezone';
    import filterExtractor from '@/helpers/TableFilterExtractor';
    import customVTColDropdown from '@/components/vue-tables-2/CustomColumnDropdown';

    export default {
        props: {
            trainingCourseId: {
                type: Number,
                default: null,
            },

            employeeId: {
                type: Number,
                default: null,
            },

            jobsiteId: {
                type: Number,
                default: null,
            },

            showAddButton: {
                type: Boolean,
                default: false,
            },

            showDistributeButton: {
                type: Boolean,
                default: false,
            },

            courseType: {
                type: String,
                default: '',
            },

            saveState: {
                type: Boolean,
                default: false,
            },

            allowFilterSaving: {
                type: Boolean,
                default: false,
            },

            filterId: {
                type: Number,
                default: null,
            },

            dashboardFilters: {
                type: Object,
                default(){
                    return null;
                },
            },

            tableConfig: {
                type: Object,
                default(){
                    return null;
                },
            },
        },

        data() {
            return {
                userTimezone: this.$store.state.user.timezone,
                tableName: 'empTrainingCourseTable'+this.courseType,
                loading: true,
                saving: false,
                exportingTable: false,
                now: moment(),
                upcoming_expiration_in_days: 30,

                filterDefs: {
                    'employee.division_id': [],
                    'employee_training_courses.training_course_id': [],
                    'training_course_catalogs.training_catalog_id': [],
                    'employee_training_courses.employee_id': {},
                    'employee_training_courses.completed_at' : '',
                    'employee_training_courses.expires_at' : '',
                    'training_course_employee.IsActive': null,
                    'employee_training_courses.completed_status': null,
                },

                filterKey: 'empCourse'+(this.courseType ? this.courseType : '')+'Filters',
            }
        },

        watch: {
            upcoming_expiration_in_days(newVal, oldVal){
                //Refresh the data
                this.$refs.empTrainingCourseTable.refresh();
            },

            appliedFilter(newVal, oldVal){
                //Call the helper
                this.checkForAppliedFilter();
            },
        },

        computed: {
            filterUrl(){
                //Checkfor Employee ID
                if(!this.employeeId && !this.trainingCourseId)
                {
                    return '/training-courses/filters?load_courses=true';
                }

                //Default
                return '/training-courses/filters';
            },

            columns(){
                //Check for training course
                if(this.trainingCourseId)
                {
                    return ['id', 'employee_person_name', 'JobsiteName', 'DivisionName', 'IsActive', 'completed_at', 'expires_at', 'last_texted_at', 'last_emailed_at', 'has_files'];
                }

                //Check for training course
                if(this.employeeId)
                {
                    return ['id', 'course_name', 'JobsiteName', 'DivisionName', 'completed_at', 'expires_at', 'last_texted_at', 'last_emailed_at', 'has_files'];
                }

                //Check for training course
                if(this.jobsiteId)
                {
                    return ['id', 'employee_person_name', 'course_name', 'completed_at', 'expires_at', 'last_texted_at', 'last_emailed_at', 'has_files'];
                }

                //Send it
                return ['id', 'employee_person_name', 'course_name', 'JobsiteName', 'DivisionName', 'completed_at', 'last_texted_at', 'last_emailed_at', 'expires_at', 'has_files'];
            },

            options(){
                return {
                    headings: {
                        id: 'Actions',
                        employee_person_name: 'Employee',
                        JobsiteName: 'Jobsite',
                        DivisionName: 'Employee Division',
                        IsActive: 'Active',
                        course_name: 'Training Course',
                        completed_at: 'Date Completed',
                        expires_at: 'Date Expires',
                        last_texted_at: 'Texted',
                        last_emailed_at: 'Emailed',
                        has_files: 'Files Attached'
                    },
                    saveState: this.saveState,
                    columnsDropdown: customVTColDropdown,
                    visibleColumns: (this.saveState ? this.$root.getVisibleColumnsFromState(this.tableName) : false),
                    sendInitialRequest: (this.filterId ? false : true),
                    orderBy: {
                        ascending: false,
                        column: 'employee_training_courses.completed_at',
                    },
                    responseAdapter: (response) => {
                        //Emit the count
                        this.$emit('empTrainingCourseListCount', response.data.count);

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Set the filters
                        data.filters = (this.dashboardFilters ? filterExtractor.extractFilters(this.dashboardFilters) : filterExtractor.extractFilters(this.filters));

                        //INclude the asset
                        data.training_course_id = this.trainingCourseId;
                        data.employee_id = this.employeeId;
                        data.jobsite_id = this.jobsiteId;

                        //Send it
                        return data;
                    },
                    uniqueKey: 'id',
                };
            },

            dataUrl(){
                //setup
                var url = '/training-courses/employee-courses';

                //Append special stuff,
                if(this.courseType)
                {
                    url += '?course_type=' + this.courseType;
                }

                //Send it
                return url;
            },

            urlDistributeEmail(){
                //Setup
                var url = '/training-courses/distribute/email';

                //Check for IDs
                if(this.jobsiteId)
                {
                    url += '?jobsite_id='+this.jobsiteId;
                }
                else if(this.employeeId)
                {
                    url += '?employee_id='+this.employeeId;
                }
                else if(this.trainingCourseId)
                {
                    url += '?training_course_id='+this.trainingCourseId;
                }

                //Send it
                return url;
            },

            urlDistributeText(){
                //Setup
                var url = '/training-courses/distribute/text';

                //Check for IDs
                if(this.jobsiteId)
                {
                    url += '?jobsite_id='+this.jobsiteId;
                }
                else if(this.employeeId)
                {
                    url += '?employee_id='+this.employeeId;
                }
                else if(this.trainingCourseId)
                {
                    url += '?training_course_id='+this.trainingCourseId;
                }

                //Send it
                return url;
            },

            filters(){
                //Check State
                if(this.$store.state.tableFilters.filters[this.filterKey])
                {
                    //Send it
                    return this.$store.state.tableFilters.filters[this.filterKey];
                }

                //Return Definition
                return JSON.parse(JSON.stringify(this.filterDefs));
            },

            appliedFilter(){
                //Send it
                return this.$store.state.appliedFilters[this.filterKey] ? this.$store.state.appliedFilters[this.filterKey] : null;
            },
        },

        methods: {

            exportTable(){
                //Loading indicator
                this.exportingTable = true;

                //Build the payload
                var payload = {
                    filters: filterExtractor.extractFilters(this.filters),
                    query: (this.$refs.empTrainingCourseTable.getRequestParams()).query,
                    orderBy: this.$refs.empTrainingCourseTable.options.orderBy.column,
                    ascending: this.$refs.empTrainingCourseTable.options.orderBy.ascending,
                    table: 'empTrainingCourseTable',
                    //columns: ['EmpFirstName', 'EmpLastName', 'course_name', 'completed_at', 'expires_at'],
                    columns: this.$refs.empTrainingCourseTable.columns,
                    export: true,
                    training_course_id: this.trainingCourseId,
                    employee_id: this.employeeId,
                    jobsite_id: this.jobsiteId,
                };

                //Check for state save
                if(this.saveState)
                {
                    payload.visible_columns = this.$root.getVisibleColumnsFromState(this.tableName);
                }

                //Send it
                this.$http.get(this.dataUrl, payload)
                    .then(response => {
                        //Open the new window
                        window.open(response.data);
                    })
                    .catch(err => {
                        //Alert the user
                        this.$toastr.error(err.response.data, 'Failed to export Completed training-courses');
                    })
                    .then(response => {
                        //reset
                        this.exportingTable = false;
                    });
            },


            isExpired(date){
                //Setup
                var expires = moment(date);

                //Send it
                return this.now.isSameOrAfter(expires);
            },

            expiresThisMonth(date){
                //Setup
                var expires = moment(date);
                var now = this.now.clone().add('days', this.upcoming_expiration_in_days);

                //Return if the Month and Year are the Same
                return now.isSameOrAfter(expires);
            },

            refreshTableData(filters) {
                //Set the data
                this.$refs.empTrainingCourseTable.setPage(1);
                //this.$refs.empTrainingCourseTable.getData();
            },

            filtersCleared(){
                //Reset
                this.refreshTableData();

                //Reset the Columns
                this.$refs.empCourseListTableFilters.resetColVis(this.$refs.empTrainingCourseTable);
            },

            setUserFilterData(){
                //Call the Helper
                this.$refs.empCourseListSavedFilters.setFilterParams(this.$refs.empTrainingCourseTable);
            },

            checkForAppliedFilter(){
                //Check if
                if(!this.saveState || !this.appliedFilter)
                {
                    return;
                }

                //Call the helper
                this.$refs.empCourseListTableFilters.setColVis(this.$refs.empTrainingCourseTable);
            },
        },

        mounted(){
            //Check for Staring Filters
            if(!this.dashboardFilters)
            {
                //Set it
                //this.$refs.incTableFilters.setFilters(this.dashboardFilters);

                //Check for a saved filter
                this.checkForAppliedFilter();

                //Check for Filter ID
                if(this.filterId)
                {
                    this.$refs.empCourseListSavedFilters.setSavedFilter(this.filterId);
                }
            }

            //Check for Table Config
            if(this.tableConfig)
            {
                //Set it
                this.$refs.empCourseListTableFilters.setColVisFromSavedFilter(this.$refs.empTrainingCourseTable, this.tableConfig);
            }
        }
    }
</script>